import { useEffect, useState, useCallback, useRef } from "react";
import { Payment } from "../../../types/features/administration/Transaction";
import useToken from "../../auth/useToken";
import { paymentsService } from "../../../services/administration/paymentsService";


export type PaymentCategory =
  | "today"
  | "all"
  | "pending"
  | "succeeded_payout_pending"
  | "succeeded_payout_failed"
  | "succeeded_payout_success"
  | "failed"
  | "by_date";

interface Props {
  userId: string;
  date?: string;
  enabled?: boolean;
}

const ITEMS_PER_PAGE = 20;

export default function usePaymentsByUser({ userId, date, enabled = true }: Props) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();
  const { token } = useToken();
  const hasFetchedRef = useRef(false);

  const getPaymentsByCategory = useCallback(
    async (page?: number, searchTerm?: string) => {
      if (!token || !enabled) {
        setLoading(false);
        return;
      }

      const targetPage = page ?? currentPage;
      const targetSearch = searchTerm !== undefined ? searchTerm : search;
      const skip = (targetPage - 1) * ITEMS_PER_PAGE;
      const pagination = { skip, limit: ITEMS_PER_PAGE, search: targetSearch || undefined };

      setLoading(true);
      try {
        const paymentsData = await paymentsService.getUserPayments(token, userId);
        const list = paymentsData?.payments || [];
        setPayments(list);
        setTotal(list?.length || 0);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setError(error);
        setLoading(false);
      }
    },
    [token, userId, date, enabled, currentPage, search]
  );

  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage(page);
      getPaymentsByCategory(page);
    },
    [getPaymentsByCategory]
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      setSearch(searchTerm);
      setCurrentPage(1);
      getPaymentsByCategory(1, searchTerm);
    },
    [getPaymentsByCategory]
  );

  useEffect(() => {
    if (enabled && token) {
      hasFetchedRef.current = true;
      setCurrentPage(1);
      getPaymentsByCategory(1, "");
    }
  }, [token, userId, date, enabled]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  return {
    payments,
    total,
    currentPage,
    totalPages,
    goToPage,
    handleSearch,
    search,
    getPayments: () => getPaymentsByCategory(),
    length: payments.length,
    loading,
    error,
    itemsPerPage: ITEMS_PER_PAGE,
  };
}
